import CTNCard from '@ctn/ui/components/ctn-card';
import CTNCardOverlayTemplate from '@ctn/ui/components/ctn-card/templates/CTNCardOverlayTemplate';
import { CTNIcon } from '@ctn/ui/components/ctn-icon';
import { type Icon } from '@ctn/ui/types/generic';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CardIconTemplate } from '@/components/CardsTemplate';
import ImageBox from '@/components/ImageBox';
import PageHeader from '@/components/PageHeader';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useApiData from '@/hooks/useApiData';
import SolicitudesOverview, { type Data as DataSolicitudesOverview } from '@/pages/Home/SolicitudesOverview';
import { type IEstadisticasSolicitud, getSolicitudEstadisticas } from '@/services/solicitudes.service';

import { getLookup } from '@/helpers/dropdown';
import { getUrlTramite } from '@/helpers/navigation';
import { type IMaestro } from '@/interfaces/model/maestros.interface';
import { type Permisos, type TramiteEstadoContratacion } from '@/interfaces/model/user.interface';

import FondoSoporte from '@/assets/images/fondo_soporte.webp';

import ListaSociosOverview from './ListaSocios';

const classNamePage = 'p-home';

type Cards = {
	id: number;
	label: string;
	icon: Icon;
	url: string;
	hoverContent: string;
};

const Home = () => {
	const navigate = useNavigate();
	const { setBreadcrumb, updateMessages } = usePageContext();
	const { loading, triggerFetch } = useApiData({ loading: ['estadisticas'] as const });
	const [estadisticas, setEstadisticas] = useState<DataSolicitudesOverview>();
	const email = 'promotores.inmobiliarios@notariado.org';
	const {
		maestrosGeneric: { estadoSolicitud },
		getTramitesContratados,
		user,
	} = useGlobalContext();

	const groupDataByState = (
		{ listaEstadisticaTipoEstadoSolicitud, total }: IEstadisticasSolicitud,
		estadoSolicitud: IMaestro[],
	): DataSolicitudesOverview => {
		const listObj = listaEstadisticaTipoEstadoSolicitud.reduce<Record<number, number>>(
			(acc, { estadoSolicitud, total }) => {
				const idState = estadoSolicitud.id;
				const accTotal = acc[idState] ?? 0;
				return { ...acc, [idState]: total + accTotal };
			},
			{},
		);

		const list = Object.entries(listObj).map(([idState, total]) => {
			const { descripcion = '' } = getLookup(Number(idState), estadoSolicitud) ?? {};
			return {
				idState: Number(idState),
				description: descripcion,
				counter: total,
			};
		});

		return { list, total };
	};

	const getCards = (tramites: TramiteEstadoContratacion[], permisos: Permisos[]): Cards[] => {
		const tramiteIdPermitidos = permisos.map(({ tipoTramite }) => tipoTramite.id);
		return tramites
			.filter((data) => tramiteIdPermitidos.includes(data.tipoTramite.id))
			.map((data) => ({
				id: data.tipoTramite.id,
				label: data.tipoTramite.descripcion,
				icon: data.icono,
				url: getUrlTramite(data),
				hoverContent: data.descripcionAmpliada,
			}));
	};

	const getData = async (estadoSolicitud: IMaestro[]) => {
		const { data } = await triggerFetch({
			fetch: getSolicitudEstadisticas,
			name: 'estadisticas',
			error: 'Ha ocurrido un error al obtener las estadísticas',
		});

		if (data) {
			setEstadisticas(groupDataByState(data, estadoSolicitud));
		}
	};

	useEffect(() => {
		setBreadcrumb([]);
		updateMessages();
	}, []);

	useEffect(() => {
		if (estadoSolicitud.length > 0) {
			getData(estadoSolicitud);
		}
	}, [estadoSolicitud]);

	const showSocios = 
    user?.nifEmisor.toLowerCase() === 'S4878368B'.toLowerCase() || 
    user?.nifEmisor.toLowerCase() === 'A78315348'.toLowerCase(); 

	return (
		<div className={`${classNamePage}`}>
			<PageHeader title={user?.tipoSectorEmisor.descripcion ?? 'Empresas y Profesionales'} />
			<div className='[ c-pnp-box-group ]'>
				<div className="[ c-pnp-box c-pnp-box--border ] [ u-mt-24 ]">
					<div className="c-pnp-box__body">
						<SolicitudesOverview
							isLoading={loading.estadisticas}
							data={estadisticas}
						/>
					</div>
				</div>
				{showSocios && (
					<div className="[ c-pnp-box c-pnp-box--border ] [ u-mt-24 ]">
						<div className="c-pnp-box__body">
							<ListaSociosOverview />
						</div>
					</div>
				)}
			</div>
			
			<section className="u-mt-32">
				<h3 className="c-pnp-heading-h3">Servicios</h3>
				<ul className={`[ o-auto-grid o-list o-fade-in ] [ ${classNamePage}__grid ] [ u-mt-24 ]`}>
					{user &&
						getCards(getTramitesContratados(), user.permisos)?.map(
							({ label, id, icon, url, hoverContent }) => (
								<li key={id}>
									<CTNCard
										aspectRatio
										bgColor="blue-50"
										className="c-pnp-card"
										overlay={
											<CTNCardOverlayTemplate
												className="c-ctn-card-icon c-ctn-card-icon--back"
												content={hoverContent}
											/>
										}
										onClick={() => navigate(url)}
									>
										<CardIconTemplate iconColor="blue-20" icon={icon} title={label} />
									</CTNCard>
								</li>
							),
						)}
				</ul>
			</section>
			<section className="u-mt-32">
				<h3 className="c-pnp-heading-h3">Soporte y ayuda</h3>
				<div className="[ o-auto-grid ] [ u-mt-24 ]">
					<ImageBox img={FondoSoporte} className={`${classNamePage}__contact`}>
						<div className={`${classNamePage}__contact-wrap`}>
							<CTNIcon icon="helpOutline" size="size-64" color="white-100" />
							<h4 className="[ c-pnp-heading-h4 ] [ u-mt-8 ]">Ayuda</h4>
							<p className="u-mt-16">
								Si tienes alguna duda sobre el portal, contáctanos por email a <a href={`mailto:${email}`} className={`${classNamePage}__link`}> {email} </a> 
							</p>
						</div>
					</ImageBox>
				</div>
			</section>
		</div>
	);
};

export default Home;
